import { useState, useRef, useCallback, useEffect, RefObject } from 'react';
import { useSelector } from 'react-redux';

import { encodeToWav, mergeChunks, floatToPCM16 } from '../../service/AudioService';
import { useOnnx } from './subHooks/useOnnx';
import { useEventCallback } from '../useEventCallback/useEventCallback';
import { RootState } from '../../redux/store';
import { apiUrl, REACT_APP_WEBSOCKET_URL } from '../../constants/constants';
import { pushFaroEvent } from '../../faroSession';

const WORKLET_URL = new URL('../../worklets/recorder.worklet.js', import.meta.url);
const SAMPLE_RATE = 16000;

interface AudioRecorderOptions {
    onRecordingEnd?: (
        blob: Blob,
        timestamps?: {
            recordingStartTimestamp: string | null;
            recordingEndTimestamp: string | null;
        },
    ) => Promise<void> | void;
    defaultVADActive?: boolean;
    onSpeechStart?: () => void;
    onSpeechEnd?: () => void;
    audioElementRef?: RefObject<HTMLAudioElement>;
}

// --- Default Options ---
const defaultOptions: AudioRecorderOptions = {
    defaultVADActive: false,
    onSpeechStart: undefined,
    onSpeechEnd: undefined,
    audioElementRef: undefined,
};

export interface IntentMetadata {
    type: 'INITIALIZATION' | 'FLAG_UPDATE';
    message: {
        cmsSessionId?: string;
        detectIntent?: boolean;
        languageCode?: string;
    };
}

interface Metadata {
    cmsSessionId: string;
    testTakerId?: string;
    productType?: string;
    testStatus?: string;
    partnerId?: string;
}

export const useAudioRecorder = (props: Partial<AudioRecorderOptions>) => {
    const options = { ...defaultOptions, ...props };
    const { selectedMic } = useSelector((state: RootState) => state.appConfigSlice);

    const { defaultVADActive, onSpeechStart, onSpeechEnd, onRecordingEnd, audioElementRef } = options;

    // --- State ---
    const [recording, setRecording] = useState(false);
    const [fullWavBlob, setFullWavBlob] = useState<Blob | null>(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // --- Refs ---
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const recordingStartTimeRef = useRef<string | null>(null);
    const recordingEndTimeRef = useRef<string | null>(null);
    const micWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
    const audioElementWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
    const fullRecordingWSRef = useRef<WebSocket | null>(null);
    const intentWsRef = useRef<WebSocket | null>(null);
    const audioElementChunksRef = useRef<Float32Array[]>([]);
    const recordedChunksRef = useRef<Float32Array[]>([]);
    const isVadActiveRef = useRef(defaultVADActive);
    const isSpeakingRef = useRef(false);
    const { testTaker, insperaDetails } = useSelector((state: RootState) => state.appConfigSlice);

    // --- ONNX Worker ---
    const { workerRef, error: onnxError, ready, initWorker, resetSileroStates } = useOnnx();

    // --- Init & Cleanup ---
    useEffect(() => {
        initWorker();
        return cleanup;
    }, [initWorker]);

    const _onSpeechStart = useEventCallback(onSpeechStart);
    const _onSpeechEnd = useEventCallback(onSpeechEnd);
    const _onRecordingEnd = useEventCallback(onRecordingEnd);

    // --- VAD Controls ---
    const startVAD = useCallback(() => {
        if (recording && !defaultVADActive) isVadActiveRef.current = true;
    }, [recording]);

    const stopVAD = useCallback(() => {
        if (recording && !defaultVADActive) {
            isVadActiveRef.current = false;
            setIsSpeaking(false);
            isSpeakingRef.current = false;
            resetSileroStates();
        }
    }, [recording, resetSileroStates]);

    const createWS = () => {
        try {
            // import constants at top-level to construct URL
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            fullRecordingWSRef.current = new WebSocket(`
                       ${REACT_APP_WEBSOCKET_URL}${apiUrl.FULL_RECORDING}?cmsSessionId=${testTaker.testTakerSessionId}`);
            fullRecordingWSRef.current.addEventListener('open', () => {
                pushFaroEvent('full_recording_websocket_opened');
                // send metadata
                const metadata = {
                    testTakerId: testTaker.testTakerId,
                    cmsSessionId: testTaker.testTakerSessionId,
                    productType: insperaDetails?.product === 'CRELLA' ? 'GT' : insperaDetails?.product,
                    testStatus: 'in-progress',
                    partnerId: insperaDetails?.partner,
                };
                if (fullRecordingWSRef.current?.readyState === WebSocket.OPEN) {
                    fullRecordingWSRef.current.send(JSON.stringify(metadata));
                }
            });
            fullRecordingWSRef.current.addEventListener('close', () => pushFaroEvent('full_recording_websocket_closed'));
            fullRecordingWSRef.current.addEventListener('error', (err) =>
                console.error('Full recording WebSocket error', err),
            );
        } catch (err) {
            console.error('Failed to create full recording websocket', err);
        }
    };

    const createIntentWS = () => {
        try {
            intentWsRef.current = new WebSocket(
                `${REACT_APP_WEBSOCKET_URL}${apiUrl.INTENT_STREAM}?cmsSessionId=${testTaker.testTakerSessionId}`,
            );
            intentWsRef.current.addEventListener('open', () => {
                const payload: IntentMetadata = {
                    type: 'INITIALIZATION',
                    message: {
                        cmsSessionId: testTaker.testTakerSessionId,
                        languageCode: 'en_GB',
                    },
                };

                intentWsRef.current?.send(JSON.stringify(payload));
            });

            intentWsRef.current.addEventListener('close', () => console.log('Intent WebSocket closed'));
            intentWsRef.current.addEventListener('error', (err) => console.error('Intent WebSocket error', err));
        } catch (err) {
            console.error('Failed to create intent websocket', err);
        }
    };

    // --- Start Recording ---
    const startFullRecording = useCallback(async () => {
        if (recording || !ready) return;
        setError(null);
        recordingStartTimeRef.current = new Date().toISOString();

        try {
            // Setup audio context and stream
            audioContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
            mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: selectedMic?.deviceId ?? 'default',
                    noiseSuppression: true,
                    echoCancellation: true,
                },
            });

            // Create intent websocket connection
            createIntentWS();

            // register worklet
            await audioContextRef.current.audioWorklet.addModule(WORKLET_URL);

            // Setup microphone worklet nodes
            const micSrc = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
            micWorkletNodeRef.current = new AudioWorkletNode(audioContextRef.current, 'recorder-worklet-processor');
            micSrc.connect(micWorkletNodeRef.current);

            // Setup audio element worklet nodes if audioRef is provided. This will record audio from the questions
            if (audioElementRef?.current) {
                const audioElementSrc = audioContextRef.current.createMediaElementSource(audioElementRef.current);
                audioElementWorkletNodeRef.current = new AudioWorkletNode(
                    audioContextRef.current,
                    'recorder-worklet-processor',
                );
                audioElementSrc.connect(audioElementWorkletNodeRef.current);
                audioElementSrc.connect(audioContextRef.current.destination);
                // create websocket for full recording (use same host as other websockets)
                createWS();

                // Send audio element PCM frames to backend as 16-bit PCM
                audioElementWorkletNodeRef.current.port.onmessage = (e) => {
                    audioElementChunksRef.current.push(new Float32Array(e.data));
                };
            }

            const pcmBuffer: any = [];
            const intentPCMBuffer: any = [];

            // PCM to worker
            micWorkletNodeRef.current.port.onmessage = (event) => {
                const rawPcm = event.data;

                if (!audioElementRef?.current) {
                    recordedChunksRef.current.push(new Float32Array(rawPcm));
                } else {
                    //merge mic and audio element chunks
                    const micChunk = new Float32Array(rawPcm);
                    const qChunk =
                        audioElementChunksRef.current.length > 0
                            ? audioElementChunksRef.current.shift()!
                            : new Float32Array(micChunk.length);
                    const merged = micChunk.map((v, i) => Math.max(-1, Math.min(1, v + qChunk[i])));
                    recordedChunksRef.current.push(merged);

                    intentPCMBuffer.push(...floatToPCM16(new Float32Array(rawPcm)));
                    sendRawPCMtoIntent(intentPCMBuffer);

                    const pcm16 = floatToPCM16(merged);
                    pcmBuffer.push(...pcm16);

                    // modify the chunks size to 1600 samples (100ms) per send to backend
                    while (pcmBuffer.length >= 800) {
                        const chunk = pcmBuffer.slice(0, 800);
                        pcmBuffer.splice(0, 800);

                        if (fullRecordingWSRef.current?.readyState === WebSocket.OPEN) {
                            fullRecordingWSRef.current.send(new Int16Array(chunk).buffer);
                        }
                    }
                }

                if (workerRef.current) {
                    workerRef.current.postMessage(
                        {
                            type: 'process',
                            data: { pcmData: rawPcm, isVadActive: isVadActiveRef.current },
                        },
                        [rawPcm.buffer],
                    );
                }
            };

            // Worker handler
            if (workerRef.current) {
                workerRef.current.onmessage = (event) => {
                    const { type, data, error } = event.data;

                    if (type === 'silero-processed' && isVadActiveRef.current) {
                        setIsSpeaking((prev) => {
                            if (prev && !data) {
                                _onSpeechEnd?.();
                            }
                            if (!prev && data) {
                                _onSpeechStart?.();
                            }

                            isSpeakingRef.current = Boolean(data);
                            return data;
                        });
                    }
                    if (type === 'error') {
                        console.error('Worker error:', error);
                    }
                };
            }

            setRecording(true);
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message ?? 'An error occurred while setting up the audio recording.');
            } else {
                setError('An unknown error occurred while setting up the audio recording.');
            }
            console.error('Error during recording setup:', err);
        }
    }, [recording, ready, resetSileroStates, workerRef, selectedMic]);

    const sendRawPCMtoIntent = (pcmBuffer: any) => {
        while (pcmBuffer.length >= 800) {
            const chunk = pcmBuffer.slice(0, 800);
            pcmBuffer.splice(0, 800);
            if (intentWsRef.current?.readyState === WebSocket.OPEN) {
                intentWsRef.current.send(new Int16Array(chunk).buffer);
            }
        }
    };

    // --- Stop Recording ---
    const stopFullRecording = useCallback(() => {
        if (!recording) return;
        const merged = mergeChunks(recordedChunksRef.current);
        const blob = encodeToWav(merged);

        setFullWavBlob(blob);
        recordingEndTimeRef.current = new Date().toISOString();

        _onRecordingEnd?.(blob, {
            recordingStartTimestamp: recordingStartTimeRef.current,
            recordingEndTimestamp: recordingEndTimeRef.current,
        });
        cleanup();
    }, [recording]);

    // --- Cleanup ---
    const cleanup = useCallback(() => {
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;

        micWorkletNodeRef.current?.disconnect();
        micWorkletNodeRef.current = null;

        audioElementWorkletNodeRef.current?.disconnect();
        audioElementWorkletNodeRef.current = null;

        // close full recording websocket
        try {
            const metadata: Metadata = {
                testTakerId: testTaker.testTakerId,
                cmsSessionId: testTaker.testTakerSessionId,
                productType: insperaDetails.product === 'CRELLA' ? 'GT' : insperaDetails.product,
                testStatus: 'completed',
                partnerId: insperaDetails?.partner,
            };
            fullRecordingWSRef.current?.send(JSON.stringify(metadata));
            fullRecordingWSRef.current?.close();
        } catch (err) {
            console.error('Error closing full recording websocket', err);
        }
        fullRecordingWSRef.current = null;

        audioContextRef.current?.close();
        audioContextRef.current = null;

        if (!defaultVADActive) {
            isVadActiveRef.current = false;
        }

        audioElementChunksRef.current = [];
        recordedChunksRef.current = [];

        resetSileroStates();

        setRecording(false);
        setIsSpeaking(false);
        isSpeakingRef.current = false;
    }, [resetSileroStates]);

    return {
        intentWsRef,
        recording,
        startFullRecording,
        stopFullRecording,
        fullWavBlob,
        recordedChunks: recordedChunksRef.current,
        isSpeaking,
        startVAD,
        stopVAD,
        createWS,
        ready,
        error: error || onnxError,
        isSpeakingRef,
        fullRecordingWSRef,
    };
};
